import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  FormHelperText,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Collapse,
  Popover,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FormatColorFillOutlinedIcon from "@mui/icons-material/FormatColorFillOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { SketchPicker } from "react-color";
import { PROCESS_CATEGORIES } from "./helpers/processCategories";

import { ACTIONS } from "./helpers/actions";

/* === DEFAULT DATA === */

const ACTION_TRIGGER_LABELS = {
  IMMEDIATLY: "Direkt när leveransstatusen ändras",
  WHEN_ALL_ARE_THE_SAME: "När alla rader i ordern har den valda leveransstatusen"
}

// Helper to map labels to menu items
const mapToMenuItems = (obj) => Object.entries(obj).map(([value, label]) => ({ value, label }));

const DEFAULT_SUPPLIER_STATUSES = PROCESS_CATEGORIES.map((c) => ({
  value: c.key,
  label: c.label,
  color: c.color,
}));

const DEFAULT_ACTIONS = Object.entries(ACTIONS).map(([value, meta]) => ({
  value,
  label: meta.label,
}));
const DEFAULT_ACTION_TRIGGERS = mapToMenuItems(ACTION_TRIGGER_LABELS);

/* Monday-inspirerade färger (egna toner, lite urvattnade) */
const PRESET_COLORS = [
  "#A7D8C9", // soft green
  "#C9E4A7", // lime pastel
  "#FFE0A3", // soft yellow
  "#FFB3A7", // coral
  "#E3B6F3", // lavender
  "#9ED6F0", // light blue
  "#B0BEC5", // grey blue
  "#F3C7A7", // peach
];

const normalizeName = (s) => (s || "").trim().toLowerCase();

/* === COLOR HELPERS === */

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const isHex = (s) => /^#?[0-9a-fA-F]{6}$/.test((s || "").trim());

const normalizeHex = (hex) => {
  const v = (hex || "").trim();
  if (!v) return "";
  return v.startsWith("#") ? v.toUpperCase() : `#${v.toUpperCase()}`;
};

const EXECUTION_MODES = {
  IMMEDIATELY: "IMMEDIATELY",
  CONFIRM_BEFORE_EXECUTION: "CONFIRM_BEFORE_EXECUTION",
};

const EXECUTION_MODE_LABELS = {
  IMMEDIATELY: "Kör åtgärden direkt",
  CONFIRM_BEFORE_EXECUTION: "Fråga innan åtgärden körs",
};

/* === COMPONENT === */

const OrderStatusPanel = ({
  open,
  mode,
  initialValue,
  existingNames,
  onClose,
  onSave,
  actions,
}) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [orderStatus, setOrderStatus] = useState("");
  const [supplierStatus, setSupplierStatus] = useState("");
  const [action, setAction] = useState(DEFAULT_ACTIONS[0].value);
  const [actionTrigger, setActionTrigger] = useState(DEFAULT_ACTION_TRIGGERS[0].value);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [touched, setTouched] = useState(false);

  const [userMustConfirmAction, setUserMustConfirmAction] = useState(false);

  /*const [executionMode, setExecutionMode] = useState(
    EXECUTION_MODES.IMMEDIATELY
  );*/

  // Popover anchor
  const [colorAnchor, setColorAnchor] = useState(null);

  useEffect(() => {
    if (!open) return;

    setTouched(false);

    const initName = initialValue?.name || "";
    const initColor = initialValue?.color || PRESET_COLORS[0];
    const initOrderStatus = initialValue?.orderStatus || "";
    const initAction = initialValue?.action || DEFAULT_ACTIONS[0].value;
    const initSupplierStatus = initialValue?.productionStatus || DEFAULT_SUPPLIER_STATUSES[0].value || "";
    const initActionTrigger = initialValue?.actionTrigger || DEFAULT_ACTION_TRIGGERS[0].value;
    const initUserMustConfirmAction = initialValue?.userMustConfirmAction ?? false;

    setName(initName);
    setColor(initColor);
    setOrderStatus(initOrderStatus);
    setSupplierStatus(initSupplierStatus);
    setAction(initAction);
    setActionTrigger(initActionTrigger);
    setUserMustConfirmAction(initUserMustConfirmAction);

    const adv = Boolean(
      initOrderStatus ||
      (initAction && initAction !== "NONE")
    );
    setShowAdvanced(adv);

    /*const initExecutionMode =
      initialValue?.actionExecutionMode ||
      EXECUTION_MODES.IMMEDIATELY;

    setExecutionMode(initExecutionMode);*/

  }, [open, initialValue]);

  const nameError = useMemo(() => {
    const n = normalizeName(name);
    if (!touched) return "";
    if (!n) return "Ange ett statusnamn.";
    const exists = existingNames
      .filter((x) => normalizeName(x) !== normalizeName(initialValue?.name || ""))
      .some((x) => normalizeName(x) === n);
    if (exists) return "Det finns redan en status med samma namn.";
    if (name.trim().length < 2) return "Statusnamnet är för kort.";
    return "";
  }, [name, existingNames, touched, initialValue]);

  const canSave = !nameError && normalizeName(name);

  const handleSave = () => {
    setTouched(true);
    if (!canSave) return;

    onSave({
      id: initialValue?.id || undefined,
      name: name.trim(),
      color: normalizeHex(color),
      //orderStatus: orderStatus || "",
      productionStatus: supplierStatus || DEFAULT_SUPPLIER_STATUSES[1].value,
      action: action || DEFAULT_ACTIONS[0].value,
      actionTrigger: actionTrigger || DEFAULT_ACTION_TRIGGERS[0].value,
      userMustConfirmAction: userMustConfirmAction,
    });
  };

  //Reset om action is removed
  useEffect(() => {
    if (action === "NONE") {
      setUserMustConfirmAction(false);
    }
  }, [action]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {mode === "edit" ? "Redigera leveransstatus" : "Skapa egen leveransstatus"}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 10, top: 10 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* STATUSNAMN + FÄRG */}
          <Box>
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <TextField
                fullWidth
                label="Namn"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder='T.ex. "Väntar på montage"'
                error={Boolean(nameError)}
                helperText={nameError || "Namnet som visas i leverantörens flöde"}
              />

              {/* FÄRGRUTA */}
              <Tooltip title="Välj färg" arrow>
                <Box
                  onClick={(e) => setColorAnchor(e.currentTarget)}
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "10px",
                    backgroundColor: color,
                    cursor: "pointer",
                    border: "1px solid rgba(0,0,0,0.18)",
                    display: "grid",
                    placeItems: "center",
                    boxShadow:
                      "inset 0 0 0 1px rgba(255,255,255,0.28), 0 1px 2px rgba(0,0,0,0.06)",
                    transition: "transform 120ms ease, box-shadow 120ms ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow:
                        "inset 0 0 0 1px rgba(255,255,255,0.30), 0 6px 16px rgba(0,0,0,0.10)",
                    },
                  }}
                >
                  <FormatColorFillOutlinedIcon sx={{ color: "rgba(0,0,0,0.55)", fontSize: 18 }} />
                </Box>
              </Tooltip>
            </Stack>

            {/* PRESET COLORS */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Snabbval:
              </Typography>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {PRESET_COLORS.map((c) => {
                  const selected = normalizeHex(c) === normalizeHex(color);
                  return (
                    <Box
                      key={c}
                      onClick={() => setColor(c)}
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "7px",
                        backgroundColor: c,
                        cursor: "pointer",
                        border: selected
                          ? "1px solid rgba(0,0,0,0.35)"
                          : "1px solid rgba(0,0,0,0.14)",
                        boxShadow: selected
                          ? "0 0 0 2px rgba(0,0,0,0.28)"
                          : "none",
                        transition: "transform 120ms ease",
                        "&:hover": { transform: "scale(1.08)" },
                      }}
                    />
                  );
                })}
              </Box>
            </Stack>
          </Box>

          <TextField
            select
            label="Processkategori"
            fullWidth
            value={supplierStatus}
            helperText={"Kopplar statusen till ett internt processteg så att systemet kan räkna ut orderns övergripande status"}
            onChange={(e) => setSupplierStatus(e.target.value)}
          >
            {DEFAULT_SUPPLIER_STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <Divider />

          {/* ADVANCED */}
          <Box>
            <Button
              onClick={() => setShowAdvanced((s) => !s)}
              startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: "none", px: 0 }}
            >
              Automatiska händelser (valfritt)
            </Button>

            <Collapse in={showAdvanced} timeout={220}>
              <Stack spacing={2} sx={{ mt: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Vad ska hända när denna status sätts?
                </Typography>


                <TextField
                  select
                  label="Automatisk åtgärd"
                  fullWidth
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                >
                  {actions.map((a) => (
                    <MenuItem key={a.value} value={a.value}>
                      {a.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="När ska åtgärden köras?"
                  fullWidth
                  value={actionTrigger}
                  helperText={"Välj om åtgärden ska ske direkt eller först när hela ordern nått detta steg."}
                  onChange={(e) => setActionTrigger(e.target.value)}
                >
                  {DEFAULT_ACTION_TRIGGERS.map((s) => (
                    <MenuItem key={s} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>

                {action !== "NONE" && (
                  <FormControl sx={{ mt: 1 }}>
                    <FormLabel
                      component="legend"
                      sx={{
                        typography: "body2",
                        color: "text.secondary",
                        mb: 1,
                      }}
                    >
                      Bekräftelse
                    </FormLabel>

                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={userMustConfirmAction}
                      onChange={(e, value) => {
                        if (value !== null) {
                          setUserMustConfirmAction(value);
                        }
                      }}
                      sx={{
                        "& .MuiToggleButton-root": {
                          textTransform: "none",
                          px: 2,
                        },
                      }}
                    >
                      <ToggleButton value={false}>
                        Kör åtgärden direkt
                      </ToggleButton>

                      <ToggleButton value={true}>
                        Fråga innan åtgärden utförs
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                )}
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {mode === "edit" ? "Spara ändringar" : "Spara status"}
        </Button>
      </DialogActions>

      {/* === COLOR POPOVER (snabbval + HEX/RGB/HSL) === */}
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { sx: { p: 1.5, borderRadius: 2, overflow: "hidden" } },
        }}
      >
        <SketchPicker
          color={color}
          onChange={(c) => setColor(c.hex)}
          presetColors={PRESET_COLORS}
          disableAlpha
        />
      </Popover>
    </Dialog>
  );
};

OrderStatusPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]),
  initialValue: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    color: PropTypes.string,
    orderStatus: PropTypes.string,
    supplierStatus: PropTypes.string,
    action: PropTypes.string,
    actionTrigger: PropTypes.string,
    actionExecutionMode: PropTypes.oneOf([
      EXECUTION_MODES.IMMEDIATELY,
      EXECUTION_MODES.CONFIRM_BEFORE_EXECUTION,
    ]),
  }),
  existingNames: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  orderStatuses: PropTypes.arrayOf(PropTypes.string),
  actions: PropTypes.arrayOf(PropTypes.string),
};

OrderStatusPanel.defaultProps = {
  mode: "create",
  initialValue: {},
  existingNames: [],  
  actions: DEFAULT_ACTIONS,
};

export default OrderStatusPanel;
